<template>
  <form @submit.prevent="submitForm">
    <h2 :class="style['user-title']">
      {{ $t('credit_card.title') }}
    </h2>

    <div :class="style['message']">
      <p :class="style.accept">{{ $t('credit_card.accept') }}</p>
      <img
        src="https://s3-ap-northeast-1.amazonaws.com/triplabot/credit-cards.png"
        alt="visa mastercard jcb amex dinersclub"
      />
    </div>

    <figure :class="style['message']" role="group">
      <p :class="style['chat-message']">
        {{ $t('credit_card.intro') }}
      </p>
    </figure>

    <label for="card-number" :class="style['user-label']">
      <input
        id="card-number"
        v-model="cardNumber"
        type="text"
        autofocus
        placeholder=" "
        :class="[style['user-input'], style['inset-label']]"
        maxlength="19"
        @input="changeCardNum"
      />

      <span :class="style['field-name']">
        {{ $t('credit_card.number') }}
      </span>
    </label>

    <div :class="[style['user-label'], style['expiration']]">
      <select
        v-model="expirationMonth"
        :class="[style['user-input'], style['half-width-input']]"
        @input="errorMessage = ''"
      >
        <option value="" hidden>{{ $t('month') }}</option>
        <option v-for="month in monthList" :key="month">{{ month }}</option>
      </select>
      <span :class="[style['field-name']]">
        {{ $t('credit_card.expiration') }}
      </span>
      <select
        v-model="expirationYear"
        :class="[style['user-input'], style['half-width-input']]"
        @input="errorMessage = ''"
      >
        <option value="" hidden>{{ $t('year') }}</option>
        <option v-for="year in yearList" :key="year">{{ year }}</option>
      </select>
    </div>

    <label for="name" :class="style['user-label']">
      <input
        id="name"
        v-model="name"
        type="text"
        placeholder=" "
        :class="style['user-input']"
        @input="errorMessage = ''"
      />
      <span :class="style['field-name']">
        {{ $t('credit_card.name') }}
      </span>
    </label>

    <label for="cvv" :class="style['user-label']">
      <input
        id="cvv"
        v-model="cvv"
        type="text"
        placeholder=" "
        maxlength="4"
        :class="[style['user-input'], style['half-width-input']]"
        @input="errorMessage = ''"
      />
      <span :class="style['field-name']">
        {{ $t('credit_card.cvv') }}
      </span>
    </label>

    <div :class="style['user-label']">
      <span v-if="showSaveCard">
        {{ $t('credit_card.save') }}
      </span>
      <checkbox
        v-if="showSaveCard"
        :class-name="'inline-checkbox'"
        :checked="saveCard"
        :toggle-check="toggleSaveCard"
      />
    </div>

    <p :class="style['error-text']">{{ errorMessage }}</p>

    <input
      v-theme
      type="submit"
      :class="[style['user-input'], style['submit-button']]"
      :value="$t('credit_card.submit')"
      :disabled="submitDisabled()"
    />
  </form>
</template>

<script>
import style from './scss/_users.scss'
import Checkbox from 'javascript/ChatWidget/UtilityComponents/Checkbox.vue'
import {
  genExpirationForCard,
  genMonthList,
  genYearList,
  currentYearMonth
} from 'Utilities/TimerHelper'
import formatCardNum from 'Utilities/normalizeCard'
import { loadScript, registerCard } from 'Utilities/thirdPartyPayment'
import { ThirdPartyPayment } from 'javascript/Constants'
import { useCreditCardPaymentMethodStore } from 'Store/credit-card-payment-method'

export default {
  name: 'NewPaymentMethod',

  components: {
    Checkbox
  },
  props: {
    settings: { type: Object, required: true },
    choosePaymentMethod: { type: Function, required: true },
    closeUserPanel: { type: Function, required: true }
  },

  setup() {
    const creditCardPaymentMethodStore = useCreditCardPaymentMethodStore()

    return { creditCardPaymentMethodStore }
  },

  data: function () {
    return {
      name: '',
      cardNumber: '',
      cvv: '',
      expirationMonth: '',
      expirationYear: '',
      isSending: false,
      errorMessage: '',
      style
    }
  },
  computed: {
    showSaveCard() {
      return this.creditCardPaymentMethodStore.showSaveCard
    },
    saveCard() {
      return this.creditCardPaymentMethodStore.saveCard
    },
    monthList() {
      return genMonthList()
    },
    yearList() {
      return genYearList()
    },
    reservationMonth() {
      return this.creditCardPaymentMethodStore.reservationMonth
    },
    thirdPartyPaymentMethod() {
      return this.settings.payment_gateway_setting.name === 'gmo'
        ? ThirdPartyPayment.GMO
        : ThirdPartyPayment.Paygent
    },
    thirdPartyPaymentShopId() {
      return this.settings.payment_gateway_setting.shop_id
    },
    thirdPartyPaymentScriptUrl() {
      return this.settings.payment_gateway_setting.script
    },
    thirdPartyPaymentToken() {
      return this.settings.payment_gateway_setting.token
    }
  },
  created() {
    loadScript({
      url: this.thirdPartyPaymentScriptUrl,
      type: this.thirdPartyPaymentMethod
    })
  },
  methods: {
    submitForm() {
      if (!this.checkExpiration()) return
      this.isSending = true
      registerCard(
        {
          shopId: this.thirdPartyPaymentShopId,
          token: this.thirdPartyPaymentToken,
          type: this.thirdPartyPaymentMethod,
          holdername: this.name,
          cardno: this.cardNumber.replace(/\s/g, ''),
          expireMonth: this.expirationMonth,
          expireYear: this.expirationYear,
          cvv: this.cvv
        },
        (registerCardResult) => {
          const resultDetails = registerCardResult.resultDetails
          const cardToken = registerCardResult.cardToken

          if (resultDetails === 'success') {
            this.$emit('submit', {
              token: cardToken,
              save_card: this.save_card
            })
            const paymentProps = {
              token: cardToken,
              save: this.saveCard,
              reservation_month: this.reservationMonth,
              reservation_id: this.creditCardPaymentMethodStore.reservationId
            }
            this.choosePaymentMethod(paymentProps, this.$t('credit_card.title'), this.messageID)
            this.errorMessage = ''
            this.closeUserPanel()
          } else {
            const CardResultDetailsToErrorMessage = {
              card: this.$t('credit_card.error.number'),
              date: this.$t('credit_card.error.expiration'),
              cvv: this.$t('credit_card.error.cvv'),
              name: this.$t('credit_card.error.name')
            }
            this.errorMessage =
              CardResultDetailsToErrorMessage[resultDetails] || this.$t('credit_card.error.unknown')
          }
          this.isSending = false
        }
      )
    },
    checkExpiration() {
      let expiredMonth = currentYearMonth()
      const result = this.reservationMonth || /^\d*(\d\d)-(\d\d)/.exec(this.reservationMonth) // YYYY-MM
      if (result && result[1] && result[2]) {
        expiredMonth = result[1] + result[2]
      }
      const userSelectedExpiration = genExpirationForCard(this.expirationYear, this.expirationMonth)
      if (expiredMonth <= userSelectedExpiration) {
        return true
      } else {
        this.errorMessage = this.$t('credit_card.error.reservation_month')
        return false
      }
    },
    toggleSaveCard() {
      this.creditCardPaymentMethodStore.updateSaveCard(!this.saveCard)
      this.errorMessage = ''
    },
    submitDisabled() {
      return (
        this.isSending ||
        !this.cardNumber ||
        !this.name ||
        !this.expirationMonth ||
        !this.expirationYear ||
        !this.cvv
      )
    },
    changeCardNum() {
      this.cardNumber = formatCardNum(this.cardNumber)
      this.errorMessage = ''
    }
  }
}
</script>
